// ui/src/pages/settings/Notifications.tsx
import React, { useState } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from '../../store/api/localApi';

const Notifications: React.FC = () => {
  const {
    isSupported,
    isIosSafari,
    isStandalone,
    permission,
    isSubscribed,
    subscribe,
    unsubscribe,
  } = usePushNotifications();
  const [busy, setBusy] = useState(false);

  const on = permission === 'granted' && isSubscribed;
  const iosNeedsInstall = isIosSafari && !isStandalone;
  const blocked = permission === 'denied';
  const canToggle = isSupported && !iosNeedsInstall && !blocked;

  const prefsEditable = on;
  const { data: prefs } = useGetNotificationPreferencesQuery();
  const [updatePrefs] = useUpdateNotificationPreferencesMutation();

  const PREF_ROWS: Array<{
    key: 'messages' | 'observations' | 'fireDrills' | 'incidents';
    label: string;
  }> = [
    { key: 'messages', label: 'Messages' },
    { key: 'observations', label: 'Observations' },
    { key: 'fireDrills', label: 'Fire drills' },
    { key: 'incidents', label: 'Incident reports' },
  ];

  // Quiet hours: a window during which push is held back. The backend has always
  // enforced this (including a window that crosses midnight, e.g. 22:00–07:00),
  // but nothing ever let anyone set it.
  //
  // Emergency medication alerts deliberately bypass quiet hours, so the copy
  // must not promise total silence.
  const quietHours = prefs?.quietHours ?? null;
  const quietOn = Boolean(quietHours);
  const [draftStart, setDraftStart] = useState('22:00');
  const [draftEnd, setDraftEnd] = useState('07:00');

  // Seed the pickers from the saved window the first time it arrives, so editing
  // starts from what is actually in force rather than the defaults.
  const [seeded, setSeeded] = useState(false);
  if (!seeded && quietHours) {
    setDraftStart(quietHours.start);
    setDraftEnd(quietHours.end);
    setSeeded(true);
  }

  const toggleQuietHours = async () => {
    if (!prefsEditable) return;
    try {
      // null CLEARS the window — omitting the key would leave it untouched.
      await updatePrefs(
        quietOn ? { quietHours: null } : { quietHours: { start: draftStart, end: draftEnd } }
      ).unwrap();
    } catch {
      /* surfaced by the mutation's own error state */
    }
  };

  const saveQuietHours = async (start: string, end: string) => {
    if (!prefsEditable || !quietOn) return;
    try {
      await updatePrefs({ quietHours: { start, end } }).unwrap();
    } catch {
      /* surfaced by the mutation's own error state */
    }
  };

  const togglePref = async (key: 'messages' | 'observations' | 'fireDrills' | 'incidents') => {
    if (!prefsEditable || !prefs) return;
    try {
      await updatePrefs({ [key]: !prefs[key] }).unwrap();
    } catch {
      /* RTK cache will refetch on the invalidate; soft-fail */
    }
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      if (on) await unsubscribe();
      else await subscribe();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Notifications
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Get notified on this device when you receive messages and updates.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BellIcon className="h-6 w-6 text-primary-500" />
              <div>
                <p className="text-base font-medium text-gray-900">Notifications on this device</p>
                <p className="text-sm text-gray-500">Push alerts even when the app isn't open.</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="notifications-toggle"
              disabled={!canToggle || busy}
              onClick={handleToggle}
              role="switch"
              aria-checked={on}
              aria-pressed={on}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                on ? 'bg-primary-600' : 'bg-gray-200'
              } ${!canToggle || busy ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
                  on ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {!isSupported && (
            <p className="mt-4 text-sm text-gray-500" data-testid="notifications-unsupported">
              This browser doesn't support push notifications.
            </p>
          )}
          {iosNeedsInstall && (
            <p className="mt-4 text-sm text-amber-700" data-testid="notifications-ios-install-help">
              On iPhone/iPad, add this app to your Home Screen first, then turn on notifications.
            </p>
          )}
          {blocked && (
            <p className="mt-4 text-sm text-amber-700" data-testid="notifications-denied-help">
              Notifications are blocked. Enable them for this site in your browser settings, then
              return here.
            </p>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-6 border border-gray-200 mt-6">
          <p className="text-base font-medium text-gray-900">What to notify me about</p>
          {!prefsEditable && (
            <p className="mt-1 text-sm text-gray-500" data-testid="notif-pref-hint">
              Enable notifications on this device first.
            </p>
          )}
          <div className="mt-4 space-y-4">
            {PREF_ROWS.map((row) => {
              const value = prefs ? prefs[row.key] : true;
              return (
                <div key={row.key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-900">{row.label}</span>
                  <button
                    type="button"
                    data-testid={`notif-pref-${row.key}`}
                    disabled={!prefsEditable}
                    role="switch"
                    aria-checked={value}
                    aria-pressed={value}
                    onClick={() => togglePref(row.key)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                      value ? 'bg-primary-600' : 'bg-gray-200'
                    } ${!prefsEditable ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
                        value ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 border border-gray-200 mt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-medium text-gray-900">Quiet hours</p>
              <p className="mt-1 text-sm text-gray-500">
                Hold back notifications overnight. Urgent medication alerts still come through.
              </p>
            </div>
            <button
              type="button"
              data-testid="quiet-hours-toggle"
              disabled={!prefsEditable}
              role="switch"
              aria-checked={quietOn}
              aria-pressed={quietOn}
              onClick={toggleQuietHours}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                quietOn ? 'bg-primary-600' : 'bg-gray-200'
              } ${!prefsEditable ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
                  quietOn ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>

          {quietOn && (
            <div className="mt-4 flex items-end gap-3" data-testid="quiet-hours-range">
              <label className="flex-1">
                <span className="block text-sm text-gray-700">From</span>
                <input
                  type="time"
                  data-testid="quiet-hours-start"
                  value={draftStart}
                  disabled={!prefsEditable}
                  onChange={(e) => setDraftStart(e.target.value)}
                  onBlur={(e) => saveQuietHours(e.target.value, draftEnd)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
              </label>
              <label className="flex-1">
                <span className="block text-sm text-gray-700">To</span>
                <input
                  type="time"
                  data-testid="quiet-hours-end"
                  value={draftEnd}
                  disabled={!prefsEditable}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  onBlur={(e) => saveQuietHours(draftStart, e.target.value)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
              </label>
            </div>
          )}

          {quietOn && draftStart > draftEnd && (
            <p className="mt-2 text-xs text-gray-500" data-testid="quiet-hours-overnight-hint">
              This window runs overnight, from {draftStart} to {draftEnd} the next day.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
